/**
 * Leitura do `mensagemErro` de um envio ao PEC e-SUS APS (E5d — AC2 e AC4).
 *
 * FONTE DO FORMATO (tratar como contrato, não como texto livre):
 * `EsusEnvioTransmissor.mensagemDe(EsusFichaException)` monta `campo + ": " + correcao`, **uma
 * linha por erro**, e `EsusErroCampo.campo` é declarado no javadoc do backend como "nome lógico
 * estável — é o que a tela e a fila leem, então trate como contrato". O catálogo abaixo espelha os
 * campos realmente emitidos por `FichaAtendimentoIndividualValidador`.
 *
 * O parser é deliberadamente tolerante: linha sem `:`, campo fora do catálogo ou **última linha
 * truncada** (o backend aplica `truncar(...)` ao gravar) caem em texto puro e nunca quebram a tela.
 * Falhas de transporte chegam sem prefixo de campo e são exatamente esse caso.
 */

/** Marcador que substitui um valor potencialmente sensível antes de renderizar. */
export const MARCADOR_REDIGIDO = '«removido»'

/** Uma linha do `mensagemErro`, já sanitizada e classificada. */
export type LinhaErro = {
  /** Campo do catálogo, ou `null` quando a linha não segue o formato `campo: correcao`. */
  campo: string | null
  /** Rótulo pt-BR do campo; ausente quando `campo` é `null`. */
  rotulo?: string
  /** Onde o operador corrige esse cadastro; ausente quando `campo` é `null`. */
  ondeCorrigir?: string
  /** O que corrigir — texto do backend, sanitizado. Igual a `texto` quando não há campo. */
  correcao: string
  /** Linha original sanitizada, para exibição de fallback. */
  texto: string
}

type EntradaCatalogo = { rotulo: string; ondeCorrigir: string }

/**
 * Campo lógico → como falar dele para a operação.
 *
 * Chaves conferidas uma a uma contra `FichaAtendimentoIndividualValidador` (os únicos `campo`
 * emitidos hoje). Campo novo no backend cai no ramo genérico até ser acrescentado aqui — degrada,
 * não quebra.
 */
export const CATALOGO_CAMPOS: Record<string, EntradaCatalogo> = {
  lotacao: {
    rotulo: 'Lotação do atendimento',
    ondeCorrigir:
      'Pode ser corrigida aqui mesmo, em "Corrigir lotação e reenviar". ' +
      'Só atendimentos encerrados depois da ativação da integração e-SUS recebem esse carimbo.',
  },
  'lotacao.funcionario.cns': {
    rotulo: 'CNS do profissional',
    ondeCorrigir: 'Cadastro do profissional → dados pessoais (campo CNS).',
  },
  'lotacao.unidade.cnes': {
    rotulo: 'CNES da unidade',
    ondeCorrigir: 'Cadastro da unidade → campo CNES.',
  },
  'lotacao.unidade.codigoIbge': {
    rotulo: 'Código IBGE do município da unidade',
    ondeCorrigir: 'Cadastro da unidade → endereço (campo código IBGE).',
  },
  'lotacao.cbo': {
    rotulo: 'CBO da lotação',
    ondeCorrigir:
      'Cadastro do profissional → Lotações (e-SUS). ' +
      'Se outra lotação do profissional estiver correta, use "Corrigir lotação e reenviar".',
  },
  'paciente.sexo': {
    rotulo: 'Sexo do paciente',
    ondeCorrigir: 'Cadastro do paciente → dados pessoais.',
  },
  'paciente.dataNascimento': {
    rotulo: 'Data de nascimento do paciente',
    ondeCorrigir: 'Cadastro do paciente → dados pessoais.',
  },
  'paciente.documento': {
    rotulo: 'CNS ou CPF do paciente',
    ondeCorrigir: 'Cadastro do paciente → documentos (informe ao menos um dos dois).',
  },
  inicio: {
    rotulo: 'Início do atendimento',
    ondeCorrigir: 'Registro do atendimento.',
  },
  termino: {
    rotulo: 'Término do atendimento',
    ondeCorrigir: 'Encerramento do atendimento.',
  },
  cids: {
    rotulo: 'CID-10 do atendimento',
    ondeCorrigir: 'Atendimento → avaliação (problemas e condições avaliadas).',
  },
  condutas: {
    rotulo: 'Conduta do atendimento',
    ondeCorrigir: 'Encerramento do atendimento → condutas.',
  },
  'medicoes.glicemiaCapilar': {
    rotulo: 'Glicemia capilar',
    ondeCorrigir: 'Atendimento → aferições.',
  },
}

/**
 * Campos em que trocar a lotação do atendimento é o conserto **certo**.
 *
 * `lotacao` = atendimento sem carimbo; `lotacao.cbo` = lotação carimbada sem CBO ou com CBO fora do
 * formato — em ambos, escolher outra lotação resolve.
 *
 * Fora daqui de propósito: `lotacao.funcionario.cns` e `lotacao.unidade.*`. Nesses a lotação está
 * certa e é o cadastro do profissional/unidade que está furado; oferecer "trocar a lotação" levaria
 * o operador a mascarar o problema real.
 */
export const CAMPOS_LOTACAO_CORRIGIVEL = ['lotacao', 'lotacao.cbo']

/**
 * Chave sensível seguida de separador explícito (`:` ou `=`) e valor.
 *
 * O separador é restrito a `:`/`=` de propósito: aceitar espaço redigiria as próprias instruções de
 * correção do backend ("Preencha a senha na configuração da unidade"), que não têm segredo nenhum.
 */
const PADRAO_CHAVE_VALOR =
  /\b(senhas?|contrasenhas?|contra-?chaves?|tokens?|authorization|password|secret)\b(\s*[:=]\s*)(?:bearer\s+|basic\s+)?\S+/gi

/** Esquema HTTP seguido do credencial (`Bearer eyJ...`), inclusive sem o cabeçalho na frente. */
const PADRAO_ESQUEMA_HTTP = /\b(bearer|basic)\s+[A-Za-z0-9\-._~+/]+=*/gi

/** JWT solto no meio do texto, sem chave nomeada antes. */
const PADRAO_JWT = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]*/g

/**
 * Defesa em profundidade do AC2: redige credencial antes de renderizar (nunca depois).
 *
 * Não é a barreira principal — a barreira é o backend, que documentadamente não coloca senha nem
 * contra-chave em `mensagemErro` (`EsusEnvioView`, `Preparacao`, `mensagemDe(EsusTransporteException)`).
 * A única superfície não controlada é `mensagemInesperada`, que carrega o `getMessage()` de uma
 * exceção arbitrária. O custo desta rede de segurança é baixo e o dia em que alguém mudar o texto do
 * backend ela já está no lugar.
 */
export const sanitizar = (texto?: string | null): string => {
  if (!texto) return ''
  return texto
    .replace(
      PADRAO_CHAVE_VALOR,
      (_trecho, chave: string, separador: string) => `${chave}${separador}${MARCADOR_REDIGIDO}`
    )
    .replace(PADRAO_ESQUEMA_HTTP, (_trecho, esquema: string) => `${esquema} ${MARCADOR_REDIGIDO}`)
    .replace(PADRAO_JWT, MARCADOR_REDIGIDO)
}

/**
 * Quebra o `mensagemErro` em linhas classificadas. Sempre sanitiza — não existe caminho que devolva
 * texto cru.
 */
export const parseMensagemErro = (texto?: string | null): LinhaErro[] => {
  const limpo = sanitizar(texto)
  if (!limpo.trim()) return []

  return limpo
    .split('\n')
    .map((linha) => linha.trim())
    .filter((linha) => linha.length > 0)
    .map((linha) => {
      const separador = linha.indexOf(':')
      if (separador > 0) {
        // Só o PRIMEIRO `:` separa: a correção pode conter outros (ex.: "HTTP 401: ...").
        const campo = linha.slice(0, separador).trim()
        const entrada = CATALOGO_CAMPOS[campo]
        if (entrada) {
          return {
            campo,
            rotulo: entrada.rotulo,
            ondeCorrigir: entrada.ondeCorrigir,
            correcao: linha.slice(separador + 1).trim(),
            texto: linha,
          }
        }
      }
      return { campo: null, correcao: linha, texto: linha }
    })
}

/** AC4: a correção de lotação só é oferecida quando é ela que conserta o erro. */
export const temErroDeLotacaoCorrigivel = (linhas: LinhaErro[]): boolean =>
  linhas.some((linha) => linha.campo !== null && CAMPOS_LOTACAO_CORRIGIVEL.includes(linha.campo))
